import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  RefreshCw,
  Search,
  Calendar,
  Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { SortableHeader } from '../../components/ui/sortable-header';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientTransactionsPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  const fetchTransactions = useCallback(async () => {
    if (!user?.client_id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API_URL}/api/client/transactions`, { headers });
      setTransactions(res.data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [token, user?.client_id]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2
    }).format(Math.abs(amount));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filteredTransactions = [...transactions].filter(tx => {
    const matchesSearch =
      String(tx.loan_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = typeFilter === 'all' || tx.type?.toLowerCase() === typeFilter.toLowerCase();

    let matchesDate = true;
    if (dateFilter !== 'all') {
      const txDate = new Date(tx.date);
      const now = new Date();
      if (dateFilter === '30days') matchesDate = (now - txDate) / 86400000 <= 30;
      else if (dateFilter === '90days') matchesDate = (now - txDate) / 86400000 <= 90;
      else if (dateFilter === 'year') matchesDate = txDate.getFullYear() === now.getFullYear();
    }

    return matchesSearch && matchesType && matchesDate;
  }).sort((a, b) => {
    const { key, direction } = sortConfig;
    let aVal = a[key];
    let bVal = b[key];
    if (['amount', 'running_balance'].includes(key)) {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    }
    if (key === 'date') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <div className="space-y-6" data-testid="transactions-loading">
        <Skeleton className="h-8 w-64" />
        <Card><CardContent className="p-6"><Skeleton className="h-64 w-full" /></CardContent></Card>
      </div>
    );
  }

  if (!user?.client_id) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="no-client-id">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <h2 className="text-xl font-bold text-[#212529] mb-2">Account Not Linked</h2>
            <p className="text-[#6C757D]">Your account is not linked to a client profile.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-transactions-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#212529] font-['Manrope']">
            Transaction History
          </h1>
          <p className="text-[#6C757D] mt-1">Cashflow movements across your loans</p>
        </div>
        <Button variant="outline" onClick={fetchTransactions} className="flex items-center gap-2" data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Search by loan number..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-input" />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full md:w-40" data-testid="type-filter">
                <Filter className="h-4 w-4 mr-2" /><SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="drawdown">Drawdowns</SelectItem>
                <SelectItem value="repayment">Repayments</SelectItem>
                <SelectItem value="interest">Interest</SelectItem>
              </SelectContent>
            </Select>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-full md:w-40" data-testid="date-filter">
                <Calendar className="h-4 w-4 mr-2" /><SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
                <SelectItem value="90days">Last 90 Days</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Transactions ({filteredTransactions.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <SortableHeader label="Date" sortKey="date" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHeader label="Loan" sortKey="loan_number" sortConfig={sortConfig} onSort={handleSort} />
                  <TableHead className="text-xs font-semibold">Type</TableHead>
                  <SortableHeader label="Amount" sortKey="amount" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
                  <SortableHeader label="Running Balance" sortKey="running_balance" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">No transactions found</TableCell>
                  </TableRow>
                ) : (
                  filteredTransactions.map((tx, index) => {
                    const isDrawdown = tx.amount > 0;
                    return (
                      <TableRow key={tx.id || index} className="hover:bg-gray-50" data-testid={`tx-row-${index}`}>
                        <TableCell className="text-sm">{formatDate(tx.date)}</TableCell>
                        <TableCell className="text-sm font-mono">{tx.loan_number || '-'}</TableCell>
                        <TableCell className="text-sm">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${isDrawdown ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                            {tx.type}
                          </span>
                        </TableCell>
                        <TableCell className={`text-sm text-right font-mono font-medium ${isDrawdown ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(tx.amount)}
                        </TableCell>
                        <TableCell className="text-sm text-right font-mono font-medium">
                          {formatCurrency(tx.running_balance)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
